'use client'

import { useState } from 'react'
import { useQuery } from '@apollo/client'
import RecipieCard from './recipieCard'
import { recipeCardSrc } from '@/lib/recipeCardSrc'
import { useBookmarkedRecipeIds } from '@/hook/useBookmarkedRecipeIds'
import { getUserId } from '@/lib/utils'
import { RECIPES_FOR_DIRECTORY_INGREDIENT, RECIPE_PREVIEW_LIMIT } from '@/lib/ingredientData'

function resolveImage(recipe: any): string {
  return recipeCardSrc(recipe)
}

export function IngredientRecipes({
  slug,
  ingredientName,
  initialRecipes = [],
  initialTotal = 0,
}: {
  slug?: string | null
  ingredientName?: string | null
  initialRecipes?: any[]
  initialTotal?: number
}) {
  const currentUserId = getUserId()
  const { bookmarkedIds, toggleBookmark } = useBookmarkedRecipeIds()
  const [expanded, setExpanded] = useState(false)

  // The first RECIPE_PREVIEW_LIMIT recipes already came from the server —
  // only fetch client-side once "see more" is clicked, so crawlers/first
  // paint see real recipe links without waiting on this at all.
  const { data } = useQuery(RECIPES_FOR_DIRECTORY_INGREDIENT, {
    variables: { slug, limit: 100 },
    skip: !slug || !expanded,
    fetchPolicy: 'no-cache',
  })

  const expandedPayload = expanded ? data?.recipesForDirectoryIngredient : undefined
  const recipes = expandedPayload?.recipes ?? initialRecipes
  const total = expandedPayload?.total ?? initialTotal

  if (!slug || !recipes.length) return null

  const remaining = Math.max(total - RECIPE_PREVIEW_LIMIT, 0)
  const label = ingredientName || 'this ingredient'

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-xl font-semibold text-[#202020] sm:text-2xl sm:leading-[29px]">
          Recipes for {label}
        </h2>
        {!expanded && remaining > 0 && (
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="inline-flex h-10 items-center justify-center rounded-full border border-[#85C349] px-6 text-sm font-medium text-[#6EAC36] transition hover:bg-[#F3FAEB]"
          >
            See {remaining} more
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {recipes.map((recipe: any) => (
          <RecipieCard
            key={recipe.recipe_id}
            link={recipe.slug ? `/recipes/${recipe.slug}` : ''}
            src={resolveImage(recipe)}
            title={recipe.recipe_name || 'Recipe'}
            time={recipe.total_time}
            rating={recipe.average_recipe_rating || 0}
            reviewCount={recipe.recipe_comments?.length || 0}
            numberOfIngredients={recipe.recipe_ingredients?.length || 0}
            recipeId={recipe.recipe_id}
            user={recipe.user}
            cardType="recipe"
            isBookmarked={bookmarkedIds.has(recipe.recipe_id)}
            onToggleBookmark={
              recipe?.user?.user_id !== currentUserId ? toggleBookmark : undefined
            }
          />
        ))}
      </div>

      {!expanded && remaining > 0 && (
        <div className="flex justify-center pt-2">
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="text-sm font-medium text-[#6EAC36] hover:underline"
          >
            See {remaining} more recipes for {label}
          </button>
        </div>
      )}
    </div>
  )
}
